<template>
  <div
    ref="rootRef"
    :class="rootClasses"
    data-cy="ds-base-input"
    :tabindex="props.disabled ? undefined : 0"
    @click="onClickRoot"
    @focus="onRootFocus"
    @focus.capture="onFocus"
    @blur.capture="onBlur"
  >
    <slot
      v-if="slots.content"
      name="content"
    />

    <div :class="DS_BASE_INPUT_CLASSES.InputWrapper">
      <DSBaseInputAffix
        v-if="normalizedPrefix"
        :affix="normalizedPrefix"
        type="prefix"
        @click="emit('prefix:click')"
      />

      <component
        :is="inputTag"
        :id="props.id"
        ref="inputRef"
        :class="inputClasses"
        :type="inputType"
        :name="props.name"
        :value="modelValue"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :required="props.required"
        :autocomplete="props.autocomplete"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :rows="props.useTextArea ? props.rows : undefined"
        :max="props.useTextArea ? undefined : props.max"
        :min="props.useTextArea ? undefined : props.min"
        :step="props.useTextArea ? undefined : props.step"
        :role="props.role"
        :aria-label="props.ariaLabel"
        :aria-invalid="props.ariaInvalid"
        :aria-controls="props.ariaControls"
        :aria-describedby="props.ariaDescribedby"
        :aria-expanded="props.ariaExpanded"
        :aria-activedescendant="props.ariaActivedescendant"
        :aria-autocomplete="props.ariaAutocomplete"
        :data-cy="`ds-base-input__input--${inputDataCySuffix}`"
        @input="onInput"
      />
    </div>

    <div
      v-if="shouldRenderSuffix"
      :class="DS_BASE_INPUT_CLASSES.Suffix"
      data-cy="ds-base-input__suffix"
    >
      <DSButton
        v-if="showClearButton"
        :variant="DS_BUTTON_VARIANT.Ghost"
        :size="DS_SIZE.Small"
        :class="DS_BASE_INPUT_CLASSES.Clearable"
        prefix-icon="cross"
        type="button"
        :aria-label="clearButtonLabel"
        :disabled="props.disabled"
        data-cy="ds-base-input__clear-button"
        @keydown.enter="props.inlineEdit && $event.stopPropagation()"
        @mousedown.stop.prevent
        @click.stop.prevent="onClear"
      />

      <DSBaseInputAffixContent
        v-if="normalizedSuffix"
        :affix="normalizedSuffix"
        type="suffix"
        base-data-cy="ds-base-input__suffix"
        @click.stop.prevent="onSuffixClick"
      />

      <slot name="suffix" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DSBaseInputAffix from "./DSBaseInputAffix.vue";
import DSBaseInputAffixContent from "./DSBaseInputAffixContent.vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { useDSBaseInput } from "../composables/useDSBaseInput";
import { DS_BASE_INPUT_CLASSES } from "../constants/classes";
import type {
  DSBaseInputEmits,
  DSBaseInputProps,
} from "@/src/design_system/form_elements/base_input/types/dsBaseInput";
import { useDSBaseInputAffix } from "@/src/design_system/form_elements/base_input/composables/useDSBaseInputAffix";
import { DS_SIZE } from "@/src/design_system/shared/types/size";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";

const props = withDefaults(defineProps<DSBaseInputProps>(), {
  disabled: false,
  readonly: false,
  required: false,
  hasError: false,
  inlineEdit: false,
  useTextArea: false,
  hiddenInput: false,
  clearable: true,
  forceShowClear: false,
  cursorPointer: false,
  wrapContent: false,
  ariaLabel: undefined,
  ariaControls: undefined,
  ariaDescribedby: undefined,
  ariaExpanded: undefined,
  ariaActivedescendant: undefined,
  ariaAutocomplete: undefined,
});

const clearButtonLabel = window.I18n.buttonClear;

const modelValue = defineModel<string | undefined | null>({ default: "" });

const emit = defineEmits<DSBaseInputEmits>();

const slots = defineSlots();

const rootRef = ref<HTMLDivElement>();

const inputDataCySuffix = computed(() => props.dataCySuffix || props.name || props.id || "null");
const inputTag = computed(() => (props.useTextArea ? "textarea" : "input"));
const inputType = computed(() => (props.useTextArea ? undefined : props.type));

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement;
  modelValue.value = target.value;
};

const onFocus = (event: FocusEvent) => {
  if (event.target === rootRef.value) return;
  if (blurCameFromElementOfBaseInput(event) && event.relatedTarget !== rootRef.value) return;
  emit("focus", event);
};

const onBlur = (event: FocusEvent) => {
  if (blurCameFromElementOfBaseInput(event)) {
    return;
  }
  emit("blur", event);
};

const blurCameFromElementOfBaseInput = (event: FocusEvent) => {
  return event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget);
};

const onRootFocus = (event: FocusEvent) => {
  if (focusCameFromInput(event)) {
    return;
  }

  if (document.activeElement !== inputRef.value) {
    focus();
  }
};

const focusCameFromInput = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as Node | null;
  return relatedTarget && relatedTarget === inputRef.value;
};

const onClickRoot = (event: MouseEvent) => {
  if (!props.disabled) {
    focus();
    emit("click", event);
  }
};

const { normalizedPrefix, normalizedSuffix } = useDSBaseInputAffix({
  prefix: computed(() => props.prefix),
  suffix: computed(() => props.suffix),
  useAffixes: computed(() => !props.useTextArea),
});

const inputClasses = computed(() => ({
  [DS_BASE_INPUT_CLASSES.Input]: true,
  [DS_BASE_INPUT_CLASSES.InputHidden]: props.hiddenInput,
  [DS_BASE_INPUT_CLASSES.InputTextarea]: props.useTextArea,
}));

const shouldRenderSuffix = computed(() => {
  if (props.useTextArea) {
    return false;
  }

  return Boolean(normalizedSuffix.value) || showClearButton.value || Boolean(slots.suffix);
});

const showClearButton = computed(() => {
  if (props.useTextArea) {
    return false;
  }

  return props.clearable && (!!modelValue.value || props.forceShowClear);
});

const { inputRef, rootClasses, focus } = useDSBaseInput(props);

const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
};

const onClear = () => {
  if (!props.clearable || props.disabled) {
    return;
  }

  emit("clear");
  if (props.inlineEdit) {
    focus();
  }
};

const onSuffixClick = (event: MouseEvent) => {
  if (props.disabled) {
    return;
  }

  emit("suffix:click", event);
};

defineExpose({
  focus,
  blur,
  getInputElement: () => inputRef.value,
});
</script>
